/**
 * Authentication Service
 * Handles login, logout, and token management
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface LoginCredentials {
  username: string;
  password: string;
}

interface WorkforceCredentials {
  empNo: string;
  userPassword: string;
}

interface AuthResponse {
  token: string;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
  };
}

class AuthService {
  private tokenKey = 'emis_auth_token';
  private userKey = 'emis_user';

  /**
   * Login user with workforce credentials
   */
  async loginWithWorkforce(credentials: WorkforceCredentials): Promise<AuthResponse> {
    const url = `${API_BASE_URL}/auth/login/`;
    console.log('[AuthService] Workforce login attempt:', { url, empNo: credentials.empNo });
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      console.log('[AuthService] Response status:', response.status);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Login failed' }));
        console.error('[AuthService] Login error:', error);
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      console.log('[AuthService] Login successful:', { username: data.user?.username });
      
      // Store token and user data
      this.setToken(data.token);
      this.setUser(data.user);
      
      return data;
    } catch (error) {
      console.error('[AuthService] Login exception:', error);
      throw error;
    }
  }

  /**
   * Login user with local credentials (fallback)
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const url = `${API_BASE_URL}/auth/login/`;
    console.log('[AuthService] Local login attempt:', { url, username: credentials.username });
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      console.log('[AuthService] Response status:', response.status);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Login failed' }));
        console.error('[AuthService] Login error:', error);
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      console.log('[AuthService] Login successful:', { username: data.user?.username });
      
      // Store token and user data
      this.setToken(data.token);
      this.setUser(data.user);
      
      return data;
    } catch (error) {
      console.error('[AuthService] Login exception:', error);
      throw error;
    }
  }

  /**
   * Logout user and clear token
   */
  async logout(): Promise<void> {
    const token = this.getToken();
    
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
          },
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    // Clear stored data
    this.clearAuth();
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Set token in storage
   */
  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    // Also store under 'token' key for compatibility
    localStorage.setItem('token', token);
    console.log('[AuthService] Token stored:', { key: this.tokenKey, token: token.substring(0, 10) + '...' });
  }

  /**
   * Get stored user data
   */
  getUser(): any | null {
    const userStr = localStorage.getItem(this.userKey);
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Set user data in storage
   */
  private setUser(user: any): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  /**
   * Clear all auth data
   */
  private clearAuth(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    // Also clear 'token' key
    localStorage.removeItem('token');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Get auth headers for API requests
   */
  getAuthHeaders(): HeadersInit {
    const token = this.getToken();
    return token ? { 'Authorization': `Token ${token}` } : {};
  }
}

const authService = new AuthService();
export default authService;